import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { StudentTopicProgressStatus } from '@edtech-ui/constants/student-course-enrollment.enum';
import { StudentProgressCourseData } from '@edtech-ui/core/models/student-courses-v2/student-course-detail-progress';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { StudentCoursesService } from '@edtech-ui/core/services/student-courses/student-courses.service';
import { ToastrService } from 'ngx-toastr';
import { filter, Subscription } from 'rxjs';

@Component({
	selector: 'app-course-side-navbar',
	standalone: true,
	imports: [CommonModule, RouterModule],
	templateUrl: './course-side-navbar.component.html',
	styleUrl: './course-side-navbar.component.scss'
})
export class CourseSideNavbarComponent implements OnInit, OnDestroy {

	isLoading = false;
	courseId!: string;
	classId!: string;
	unitId: string | null = null;
	topicId: string | null = null;
	StudentTopicProgressStatus = StudentTopicProgressStatus;
	courseDetails: StudentProgressCourseData | null = null;
	subscription = new Subscription();
	isSidebarOpen = false;
	PermissionForStudents = PermissionForStudents;

	constructor(
		private router: Router,
		private _studentCourseService: StudentCoursesService,
		private route: ActivatedRoute,
		private _authService: AuthServiceService,
		private _toastr :ToastrService
	) { }

	ngOnInit() {

		if (!this._authService.hasPermission(PermissionForStudents.COURSE_VIEW)) {
			this._toastr.error('You do not have permission to access this course');
			this.router.navigate(['/student-dashboard']);
			return;
		}

		this.subscription.add(
			this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
				const childRoute = this.getChild(this.route);
				this.unitId = childRoute.snapshot.paramMap.get('unitId');
				this.topicId = childRoute.snapshot.paramMap.get('topicId');

				const parentRoute = childRoute.parent ?? this.route;
				this.courseId = parentRoute.snapshot.paramMap.get('courseId') || this.courseId;
				this.classId = parentRoute.snapshot.paramMap.get('classId') || this.classId;
			})
		);

		this.subscription.add(
			this.route.firstChild?.paramMap.subscribe(params => {
				this.unitId = params.get('unitId');
				this.topicId = params.get('topicId');
			}) ?? new Subscription
		);

		this.subscription.add(
			this._studentCourseService.courseDetails$.subscribe(details => {
				if (details) this.courseDetails = details;
			})
		);
	}

	trackByUnitId(index: number, unit: any) {
		return unit.unit_id;
	}

	navigateToLesson(unitId: string, topicId: string) {
		this.router.navigate(['course-description', unitId, topicId], { relativeTo: this.route.parent });
	}

	isSectionActive(unit: any): boolean {
		return unit.topics?.some((topic: any) => topic.topic_id === this.topicId && unit.unit_id === this.unitId);
	}

	backToCourseDetailPage() {
		this.router.navigate(['student-dashboard/course-details', this.courseId]);
	}

	isUnitCompleted(unit: any): boolean {
		return unit.topics?.length && unit.topics.every((topic: any) => topic.status === 'COMPLETED');
	}

	getChild(route: ActivatedRoute): ActivatedRoute {
		if (route.firstChild) {
			return this.getChild(route.firstChild);
		} else {
			return route;
		}
	}

	toggleSidebar() {
		this.isSidebarOpen = !this.isSidebarOpen;
	}

	closeSidebar() {
		this.isSidebarOpen = false;
	}

	get completedTopicsCount(): number {
		if (!this.courseDetails?.units) return 0;
		return this.courseDetails.units.reduce((acc, unit) => {
			return acc + (unit.topics?.filter((t: any) => t.status === StudentTopicProgressStatus.COMPLETED).length || 0);
		}, 0);
	}

	get totalTopicsCount(): number {
		if (!this.courseDetails?.units) return 0;
		return this.courseDetails.units.reduce((acc, unit) => {
			return acc + (unit.topics?.length || 0);
		}, 0);
	}

	openSidebar() {
		this.isSidebarOpen = true;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}